import { inject, Injectable } from '@angular/core';
import { isDefined, mapAsKeys } from '@sama/js-core';
import { pickBy, uniq } from 'lodash';
import { combineLatest, map } from 'rxjs';
import { BrowserControlService } from '../../../../../cross-team-shared/misc-services/browser-control.service';
import {
  keys,
  keys as numberKeys,
} from '../../../../../cross-team-shared/misc-utils/keys';
import { OutputInfoState } from '../../../../../outputs/annotation/annotation-shared/annotation-providers';
import { IN_PROGRESS_INDEX } from '../../../../../team-apis/core/reserved-shape-indexes';
import { Selection } from '../../../../h3ml/state/fusion-state';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { getOutputAnnotationInfoStore } from '../../../../h3ml/state/task-workspace-store-utils';
import { ObjectScopingService } from '../../../../shared/misc-services/object-scoping.service';
import { ShapeService } from '../../../../shared/misc-services/shape.service';

@Injectable()
export class RootShapeSelectionService {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private objectScopingService = inject(ObjectScopingService);
  private shapeService = inject(ShapeService);
  private browserControlService = inject(BrowserControlService);

  getNestedSelection(
    includeInProgressIndex = false,
    shapeSelection = this.taskWorkspaceStore.state().shapeSelection,
    objectSelection = this.taskWorkspaceStore.state().objectSelection,
  ): Selection {
    const nestedSelectedShapes = numberKeys(objectSelection)
      //  case where we are trying to look for an object that is being removed
      .filter((objectId) =>
        isDefined(this.taskWorkspaceStore.state().objects[objectId]),
      )
      .flatMap((objectId) =>
        this.objectScopingService.getNestedShapes(objectId),
      );

    let selectedShapeIndexes = [
      ...numberKeys(shapeSelection),
      ...nestedSelectedShapes.map((shape) => shape.index),
    ];

    selectedShapeIndexes = selectedShapeIndexes.filter((shapeIndex) =>
      includeInProgressIndex ? true : shapeIndex !== IN_PROGRESS_INDEX,
    );

    return mapAsKeys(uniq(selectedShapeIndexes), () => true);
  }

  getSelectedShapes() {
    const selection = this.getNestedSelection(false);
    return this.shapeService.getShapes(numberKeys(selection));
  }

  getNestedSelectionForOutput(outputName: string) {
    const rootSelection = keys(this.getNestedSelection());
    const outputStore = getOutputAnnotationInfoStore(
      this.taskWorkspaceStore,
      outputName,
    );
    const shapesForOutput = outputStore.state().shapes;
    return rootSelection.filter((shapeIndex) => shapesForOutput[shapeIndex]);
  }

  getSelection$(includeInProgressIndex = false) {
    const shapeSelection$ = this.taskWorkspaceStore('shapeSelection').$;
    const selectedObjectIds$ = this.taskWorkspaceStore('objectSelection').$;

    return combineLatest([shapeSelection$, selectedObjectIds$]).pipe(
      map(([shapeSelection, selectedObjectIds]) =>
        this.getNestedSelection(
          includeInProgressIndex,
          shapeSelection,
          selectedObjectIds,
        ),
      ),
    );
  }

  loadSelectionFromQueryParameters() {
    const queryParams = new URLSearchParams(
      this.browserControlService.getQueryParameters(),
    );

    this.taskWorkspaceStore('shapeSelection').set(
      mapAsKeys(
        queryParams
          .get('selectedShapes')
          ?.split(',')
          .filter(Boolean)
          .map(Number),
        () => true,
      ),
    );
  }
}

export function filterSelectionByOutput(
  outputInfoState: OutputInfoState,
  selection: Selection,
) {
  const shapes = outputInfoState.shapes;
  return pickBy(
    selection,
    (_, shapeIndex) =>
      !!shapes[Number(shapeIndex)] || Number(shapeIndex) === IN_PROGRESS_INDEX,
  );
}
